import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, Repeat } from "lucide-react";
import {
  useDeleteWithUndoController,
  useGetOne,
  useGetRecordRepresentation,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { DateField } from "@/components/admin/date-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { getTranslatedConfigLabel } from "../misc/getTranslatedConfigLabel";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { Company, Contact, Task as TData } from "../types";
import { CompleteCampaignTaskDialog } from "../campaigns/CompleteCampaignTaskDialog";
import { TaskEdit } from "./TaskEdit";
import { TaskEditSheet } from "./TaskEditSheet";
import { useCanManageTask } from "./useCanManageTask";
import { useIsMobile } from "@/hooks/use-mobile";

export const Task = ({
  task,
  showContact,
}: {
  task: TData;
  showContact?: boolean;
}) => {
  const isMobile = useIsMobile();
  const canManage = useCanManageTask(task);
  const assigneeName = useGetSalesName(task.sales_id, {
    enabled: task.sales_id != null,
  });
  const { taskTypes } = useConfigurationContext();
  const notify = useNotify();
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const getContactRepresentation = useGetRecordRepresentation("contacts");
  const getCompanyRepresentation = useGetRecordRepresentation("companies");
  const { data: campaign } = useGetOne(
    "campaigns",
    { id: task.campaign_id! },
    { enabled: !!task.campaign_id },
  );

  const [openEdit, setOpenEdit] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const [update, { isPending: isUpdatePending, isSuccess, variables }] =
    useUpdate();
  const { handleDelete } = useDeleteWithUndoController({
    record: task,
    redirect: false,
    mutationOptions: {
      onSuccess() {
        notify("resources.tasks.deleted", {
          undoable: true,
        });
      },
    },
  });

  const handleEdit = () => {
    setOpenEdit(true);
  };

  const handleCheckedChange = (checked: boolean) => {
    if (!canManage) {
      return;
    }

    if (checked && task.campaign_id && !task.done_date) {
      setCompleteDialogOpen(true);
      return;
    }

    update("tasks", {
      id: task.id,
      data: {
        done_date: checked ? new Date().toISOString() : null,
        ...(task.campaign_id && !checked ? { completion_note: null } : {}),
      },
      previousData: task,
    });
  };

  const handleMobileToggle = () => {
    if (!canManage) {
      return;
    }

    if (task.done_date) {
      handleCheckedChange(false);
      return;
    }
    if (task.campaign_id) {
      setCompleteDialogOpen(true);
      return;
    }
    handleCheckedChange(true);
  };

  useEffect(() => {
    // We do not want to invalidate the query when a tack is checked or unchecked
    if (
      isUpdatePending ||
      !isSuccess ||
      variables?.data?.done_date != undefined
    ) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["tasks", "getList"] });
  }, [queryClient, isUpdatePending, isSuccess, variables]);

  const labelId = `checkbox-list-label-${task.id}`;

  return (
    <>
      <div className="flex items-start justify-between">
        <div
          className="flex items-start gap-2 flex-1"
          onClick={isMobile && canManage ? handleMobileToggle : undefined}
        >
          <Checkbox
            id={labelId}
            checked={!!task.done_date}
            onCheckedChange={handleCheckedChange}
            disabled={!canManage || isUpdatePending}
            className="mt-1"
          />
          <div className={`flex-grow ${task.done_date ? "line-through" : ""}`}>
            <div className="text-sm">
              {task.type && task.type !== "none" && (
                <>
                  <span className="font-semibold text-sm">
                    {(() => {
                      const matchedTaskType = taskTypes.find(
                        (taskType) => taskType.value === task.type,
                      );
                      return matchedTaskType
                        ? getTranslatedConfigLabel(
                            matchedTaskType,
                            "taskTypes",
                            translate,
                          )
                        : task.type;
                    })()}
                  </span>
                  &nbsp;
                </>
              )}
              {task.text}
              {task.recurrence_id && (
                <span className="inline-flex items-center gap-1 ml-2 text-xs text-muted-foreground">
                  <Repeat className="w-3 h-3" />
                  {translate("resources.tasks.recurrence.badge")}
                </span>
              )}
              {task.campaign_id && campaign && (
                <Link
                  to={`/campaigns/${task.campaign_id}/show`}
                  className="inline-flex ml-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Badge variant="outline" className="text-xs">
                    {campaign.name}
                  </Badge>
                </Link>
              )}
            </div>
            <div className="text-sm text-muted-foreground">
              {translate("resources.tasks.fields.due_short")}
              &nbsp;
              <DateField source="due_date" record={task} showDate showTime />
              {task.sales_id && assigneeName && (
                <>
                  {" · "}
                  {translate("resources.tasks.assigned_to", {
                    name: assigneeName,
                    _: "Tildelt: %{name}",
                  })}
                </>
              )}
              {showContact && task.contact_id && (
                <ReferenceField<TData, Contact>
                  source="contact_id"
                  reference="contacts"
                  record={task}
                  link="show"
                  className="inline text-sm text-muted-foreground"
                  render={({ referenceRecord }) => {
                    if (!referenceRecord) return null;
                    return (
                      <>
                        {" "}
                        {translate("resources.tasks.regarding_contact", {
                          name: getContactRepresentation(referenceRecord),
                        })}
                      </>
                    );
                  }}
                />
              )}
              {showContact && !task.contact_id && task.company_id && (
                <ReferenceField<TData, Company>
                  source="company_id"
                  reference="companies"
                  record={task}
                  link="show"
                  className="inline text-sm text-muted-foreground"
                  render={({ referenceRecord }) => {
                    if (!referenceRecord) return null;
                    return (
                      <>
                        {" "}
                        {translate("resources.tasks.regarding_company", {
                          name: getCompanyRepresentation(referenceRecord),
                          _: "regarding %{name}",
                        })}
                      </>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {canManage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 pr-0! size-8 cursor-pointer"
                aria-label={translate("resources.tasks.actions.title")}
              >
                <MoreVertical className="size-5 md:size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={() => {
                  update("tasks", {
                    id: task.id,
                    data: {
                      due_date: new Date(Date.now() + 24 * 60 * 60 * 1000)
                        .toISOString()
                        .slice(0, 10),
                    },
                    previousData: task,
                  });
                }}
              >
                {translate("resources.tasks.actions.postpone_tomorrow")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={() => {
                  update("tasks", {
                    id: task.id,
                    data: {
                      due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                        .toISOString()
                        .slice(0, 10),
                    },
                    previousData: task,
                  });
                }}
              >
                {translate("resources.tasks.actions.postpone_next_week")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={handleEdit}
              >
                {translate("ra.action.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={handleDelete}
              >
                {translate("ra.action.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isMobile ? (
        <TaskEditSheet
          taskId={task.id}
          open={openEdit}
          onOpenChange={setOpenEdit}
        />
      ) : (
        <TaskEdit taskId={task.id} open={openEdit} close={handleCloseEdit} />
      )}

      {task.campaign_id && (
        <CompleteCampaignTaskDialog
          task={task}
          open={completeDialogOpen}
          onOpenChange={setCompleteDialogOpen}
        />
      )}
    </>
  );
};
